"use client";

import { useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useStoreStore } from '@/stores/store-store';
import { ROLE_INFO, type UserRole } from '@/lib/rbac';
import { ChevronUp, ChevronDown, Bug } from 'lucide-react';

const ALL_ROLES: UserRole[] = ['owner', 'admin', 'marketing', 'cs_agent', 'finance', 'developer'];

const ROLE_LABELS: Record<UserRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  marketing: 'Marketing',
  cs_agent: 'CS Agent',
  finance: 'Finance',
  developer: 'Developer',
};

export function DevToolbar() {
  const [expanded, setExpanded] = useState(false);
  const { user, switchRole } = useAuthStore();
  const { stores, activeStoreId, isGlobalView, setActiveStore, toggleGlobalView } = useStoreStore();

  // Only show in development
  if (process.env.NODE_ENV !== 'development') return null;
  if (!user) return null;

  const roleInfo = ROLE_INFO[user.role];
  const activeStore = stores.find(s => s.id === activeStoreId);

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="fixed bottom-4 right-4 z-[9999] bg-gray-900 text-white rounded-full w-10 h-10 flex items-center justify-center shadow-lg hover:bg-gray-800 transition-colors"
        title="Dev Toolbar"
      >
        <Bug className="w-5 h-5" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-900 text-white border-t border-gray-700 shadow-2xl">
      <div className="max-w-7xl mx-auto px-4 py-2">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Bug className="w-4 h-4 text-yellow-400" />
            <span className="text-xs font-bold text-yellow-400 uppercase tracking-wider">Dev Toolbar</span>
          </div>
          <button onClick={() => setExpanded(false)} className="text-gray-400 hover:text-white">
            <ChevronDown className="w-4 h-4" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-wrap items-center gap-4 pb-1">
          {/* Role Switcher */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Role:</span>
            <div className="flex gap-1">
              {ALL_ROLES.map(role => (
                <button
                  key={role}
                  onClick={() => switchRole(role)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    user.role === role
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {ROLE_LABELS[role]}
                </button>
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-700" />

          {/* Store Switcher */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">Store:</span>
            <select
              value={isGlobalView ? 'global' : activeStoreId}
              onChange={e => {
                if (e.target.value === 'global') {
                  toggleGlobalView();
                } else {
                  setActiveStore(e.target.value);
                }
              }}
              className="bg-gray-700 text-white text-xs rounded px-2 py-1 border border-gray-600"
            >
              <option value="global">🌐 All Stores</option>
              {stores.map(s => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.tier})
                </option>
              ))}
            </select>
          </div>

          {/* Divider */}
          <div className="w-px h-6 bg-gray-700" />

          {/* Current State */}
          <div className="flex items-center gap-3 text-xs text-gray-400">
            <span>
              User: <span className="text-white">{user.name}</span>
            </span>
            <span>
              Email: <span className="text-gray-300">{user.email}</span>
            </span>
            <span className={`px-1.5 py-0.5 rounded ${roleInfo.bgColor} ${roleInfo.color} font-medium`}>
              {ROLE_LABELS[user.role]}
            </span>
            <span className={`px-1.5 py-0.5 rounded font-medium ${
              useAuthStore.getState().isApiMode 
                ? 'bg-green-900 text-green-300' 
                : 'bg-orange-900 text-orange-300'
            }`}>
              {useAuthStore.getState().isApiMode ? 'API' : 'Mock'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
